import type { Invoice, InvoiceStatus } from '../types';
import type { LateFeeType, Merchant } from '../../merchants/types';

/**
 * Platform-wide defaults for the billing settings a merchant can override (PE-2105).
 * No platform-default endpoint exists in the contract this ticket was built against —
 * only `merchants PATCH /:id` for a per-merchant override — so these are used purely as
 * client-side fallbacks/placeholders wherever a merchant hasn't set its own value.
 * Values confirmed against watchman: invoicePriceExpiryDays null ⇒ 7-day platform default;
 * lateFeeGracePeriodDays default 5 (watchman internal-merchants seed + contract-terms DTO).
 */
export const PLATFORM_DEFAULT_PRICE_EXPIRY_DAYS = 7;
export const PLATFORM_DEFAULT_LATE_FEE_GRACE_PERIOD_DAYS = 5;

/**
 * TCS issuer details for TCS-issued documents (platform → merchant billing). Hardcoded per
 * the PE-2105 ticket contract — not sourced from an API since TCS is the platform itself.
 */
export const TCS_ISSUER = {
  name: 'The Collective Synergy',
  addressLines: ['4, Ogudu GRA, Gate A, Salaudeen Akano Street', 'Lagos, Lagos State, Nigeria'],
  phone: '+234 810 994 9895',
  email: 'admin@thecollectivesynergy.com',
  website: 'thecollectivesynergy.com',
};

/**
 * Distinguishes a live invoice (pending/unpaid/expired/overdue) from a receipt issued for a
 * payment that already happened. Prefers the server's `documentType`; falls back to a
 * heuristic (PAID + no dueDate, the shape `POST /backdated-receipt` produces) for records
 * from before the field existed.
 */
export function isReceipt(invoice: Pick<Invoice, 'documentType' | 'status' | 'dueDate'>): boolean {
  if (invoice.documentType) return invoice.documentType === 'RECEIPT';
  return invoice.status === 'PAID' && !invoice.dueDate;
}

/** The date a pending invoice's quoted price stops being valid. */
export function priceExpiryDate(
  invoice: Pick<Invoice, 'issuedAt' | 'createdAt' | 'priceExpiryDays'>,
  merchant?: Pick<Merchant, 'invoicePriceExpiryDays'> | null,
): Date | null {
  const anchor = invoice.issuedAt ?? invoice.createdAt;
  if (!anchor) return null;
  const days = invoice.priceExpiryDays ?? merchant?.invoicePriceExpiryDays ?? PLATFORM_DEFAULT_PRICE_EXPIRY_DAYS;
  const d = new Date(anchor);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Client-computed status overlay. Layers EXPIRED on top of a pending invoice whose
 * price-expiry window has lapsed (the backend may also set this directly via
 * `PATCH /:id/status` — treated identically once it does). Receipts and invoices that are
 * already resolved (paid/cancelled/void) are never overlaid.
 */
export function effectiveStatus(
  invoice: Invoice,
  merchant?: Pick<Merchant, 'invoicePriceExpiryDays'> | null,
): InvoiceStatus {
  if (invoice.status !== 'DRAFT' && invoice.status !== 'SENT') return invoice.status;
  if (isReceipt(invoice)) return invoice.status;
  const expiry = priceExpiryDate(invoice, merchant);
  if (expiry && expiry.getTime() < Date.now()) return 'EXPIRED';
  return invoice.status;
}

export function isOverdue(invoice: Pick<Invoice, 'dueDate' | 'status'>): boolean {
  if (!invoice.dueDate) return false;
  if (invoice.status === 'PAID' || invoice.status === 'VOID' || invoice.status === 'CANCELLED') return false;
  return new Date(invoice.dueDate).getTime() < Date.now();
}

export function daysOverdue(invoice: Pick<Invoice, 'dueDate'>): number {
  if (!invoice.dueDate) return 0;
  const ms = Date.now() - new Date(invoice.dueDate).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

export interface LateFeeResult {
  /** Whether a fee has actually started accruing (past due date + grace period). */
  accruing: boolean;
  /** Accrued fee amount in minor units, 0 when not yet accruing. */
  amount: number;
  daysOverdue: number;
  graceDaysRemaining: number;
}

/**
 * Computes the accrued late-payment penalty for an overdue invoice, given the merchant's
 * (or platform default) lateFeeType/lateFeeAmount/lateFeeGracePeriodDays. Returns a zero
 * result for receipts, paid/void/cancelled invoices, or invoices with no merchant-configured
 * fee (lateFeeType unset — no fee is charged rather than guessing a default rate).
 */
export function computeLateFee(
  invoice: Pick<Invoice, 'dueDate' | 'status' | 'documentType' | 'totalAmount'>,
  merchant?: Pick<Merchant, 'lateFeeType' | 'lateFeeAmount' | 'lateFeeGracePeriodDays'> | null,
): LateFeeResult {
  const grace = merchant?.lateFeeGracePeriodDays ?? PLATFORM_DEFAULT_LATE_FEE_GRACE_PERIOD_DAYS;
  const overdueDays = isOverdue(invoice) ? daysOverdue(invoice) : 0;
  const graceDaysRemaining = Math.max(0, grace - overdueDays);

  if (isReceipt(invoice) || !overdueDays || overdueDays <= grace || !merchant?.lateFeeType) {
    return { accruing: false, amount: 0, daysOverdue: overdueDays, graceDaysRemaining };
  }

  const type: LateFeeType = merchant.lateFeeType;
  const rawAmount = merchant.lateFeeAmount ?? 0;
  // watchman: PERCENTAGE lateFeeAmount is basis points of the total (150 = 1.5%); FLAT is a
  // minor-currency-unit (kobo) amount.
  const amount = type === 'PERCENTAGE'
    ? Math.round((invoice.totalAmount * rawAmount) / 10000)
    : Math.round(rawAmount);

  return { accruing: amount > 0, amount, daysOverdue: overdueDays, graceDaysRemaining };
}
